import axios, { CancelToken } from 'axios';
import API from './config';
import { BARLEY_DENSITY_MODEL, Coordinates } from 'utils/barleyDensityHelpers';

export interface BarleyDensityField {
  models: Array<{ name: string; version: string }>;
  location: {
    geometry: {
      type: 'Point';
      coordinates: Coordinates;
    };
  };
  planting_date: string;
  variety: string;
  yield_level: string;
  planting_condition: string;
  grass_weed_pressure?: boolean;
}

export interface BarleyDensityRequestBody {
  request_version: string;
  fields: BarleyDensityField[];
}

export interface BarleyDensityFeature {
  type: string;
  unit: string;
  value: number;
}

export interface BarleyDensityPrediction {
  feature_category: string;
  features: BarleyDensityFeature[];
}

export interface BarleyDensityResultMetadata {
  type?: string;
  models?: Array<{ name: string; version: string }>;
  location?: { country?: string };
  variety?: string;
  yield_level?: { name: string; value: number };
  planting_date?: string;
  planting_conditions?: { name: string; value: number };
  grass_weed_pressure?: { value: number; mode: string };
}

export interface BarleyDensityResult {
  metadata: BarleyDensityResultMetadata;
  predictions: BarleyDensityPrediction[];
}

export interface BarleyDensityResponse {
  results: BarleyDensityResult[];
}

export interface BuildBarleyDensityFieldInput {
  centroid: Coordinates;
  plantingDate: string;
  variety: string;
  yieldLevel: string;
  plantingCondition: string;
  /** Only forwarded for Germany / Great Britain. */
  grassWeedPressure?: boolean;
}

/**
 * Builds a single `fields[]` entry in the upstream payload shape.
 */
export const buildBarleyDensityField = (
  input: BuildBarleyDensityFieldInput
): BarleyDensityField => {
  const field: BarleyDensityField = {
    models: [BARLEY_DENSITY_MODEL],
    location: {
      geometry: {
        type: 'Point',
        coordinates: input.centroid,
      },
    },
    planting_date: input.plantingDate,
    variety: input.variety,
    yield_level: input.yieldLevel,
    planting_condition: input.plantingCondition,
  };

  if (typeof input.grassWeedPressure === 'boolean') {
    field.grass_weed_pressure = input.grassWeedPressure;
  }

  return field;
};

class BarleyDensityService {
  private _api;

  constructor() {
    this._api = API(`${process.env.REACT_APP_PROXY_LAYER_URL}/proxy-layer/v1`);
  }

  cancelToken() {
    return axios.CancelToken.source();
  }

  async fetchBarleyDensity(
    body: BarleyDensityRequestBody,
    cancelToken?: CancelToken
  ): Promise<BarleyDensityResponse> {
    const response = await this._api.post('/barley-density', body, {
      cancelToken,
    });
    return response.data as BarleyDensityResponse;
  }
}

export default new BarleyDensityService();
